import React, { createContext, ReactNode, useContext, useState, useEffect } from 'react'
import { EventClickArg, EventContentArg } from '@fullcalendar/react'
import { pdf } from '@react-pdf/renderer'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import moment from 'moment'
import { useHistory } from 'react-router-dom';
import { sortBy } from 'lodash';

import { AnalystService } from '../services/methods/AnalystService'
import { OsService } from '../services/methods/OsService'
import { ScheduleService } from '../services/methods/ScheduleService'

import MyPdf from '../components/MyPdf'
import Os from '../components/Os'

import { ISchedule, IScheduleStruct, IProjects, Coordinators } from '../interfaces/ISchedule'
import { IAnalyst } from '../interfaces/IAnalyst'

import { getBase64 } from '../utils/getBase64'
import { useUser } from './user'

interface ScheduleProviderProps {
  children: ReactNode
}

interface IScheduleContextData {
  month: number;
  year: number;
  analystName: string;
  calendarHeight: number;
  isOpenLaunchOSModal: boolean;
  isOpenOutgoingOSModal: boolean;
  isVisualOutgoingOSModal: boolean;
  launchOsModalData: ISchedule;
  fullCalendarData: ISchedule[];
  schedules: ISchedule[];
  currentDate: string;
  isLoading: boolean;
  schedulesInfo: IScheduleStruct;
  coordinators: Coordinators[];
  filter: 'abertas' | 'apontadas' | 'remoto' | 'presencial' | '';
  setFilter: (filter: 'abertas' | 'apontadas' | 'remoto' | 'presencial' | '') => void;
  handleEventClick: (clickInfo: EventClickArg) => void;
  renderEventContent: (eventContent: EventContentArg) => void;
  loadSchedulesData: (month: number, year: number, analystCode?: string) => Promise<void>;
  handleCloseLaunchOSModal: () => void;
  handleCloseOutgoingOSModal: () => void;
  setMonth: (month: number) => void;
  setYear: (year: number) => void;
  setFullCalendarData: (schedules: ISchedule[]) => void;
  setCurrentDate: (date: string) => void;
  setIsLoading: (val: boolean) => void;
  updateSchedulesData: (analista: IScheduleStruct) => void;
  filterSchedules: (type: 'abertas' | 'apontadas' | 'remoto' | 'presencial' | '', data: ISchedule[]) => void;
  receiveIsUpdate: boolean;
  setReceiveIsUpdate: (value: boolean) => void;
  loadReportData: (month: number, year: number) => Promise<void>;
  projectsReport: IProjects[];
  schedulesReport: ISchedule[];
  // Analista
  analistas: IAnalyst[];
  fetchAnalystLoad: boolean;
  handleFetchAnalyst: () => Promise<void>;
  setAnalyst: (value: string) => void;
  analyst: string;
}

const ScheduleContext = createContext({} as IScheduleContextData)

function ScheduleProvider({ children }: ScheduleProviderProps) {
  const analystService: AnalystService = new AnalystService();
  const scheduleService: ScheduleService = new ScheduleService();
  const osService: OsService = new OsService();
  
  const [month, setMonth] = useState(Number(moment().format('DD')) > 25 ? (Number(moment().format('MM')) + 1) > 12 ? 1 : (Number(moment().format('MM')) + 1) : Number(moment().format('MM')))
  const [year, setYear] = useState(Number(moment().format('DD')) > 25 && (Number(moment().format('MM')) + 1) > 12 ? (Number(moment().format('YYYY')) + 1) : Number(moment().format('YYYY')))
  const [analystName, setAnalystName] = useState('');
  const [schedulesInfo, setSchedulesInfo] = useState<IScheduleStruct>({} as IScheduleStruct)
  const [calendarHeight, setCalendarHeight] = useState(0)
  const [isOpenLaunchOSModal, setIsOpenLaunchOSModal] = useState(false)
  const [isOpenOutgoingOSModal, setIsOpenOutgoingOSModal] = useState(false)
  const [isVisualOutgoingOSModal, setIsVisualOutgoingOSModal] = useState(false)
  const [launchOsModalData, setLaunchOsModalData] = useState<ISchedule>({} as ISchedule)
  const [fullCalendarData, setFullCalendarData] = useState<ISchedule[]>([])
  const [schedules, setSchedules] = useState<ISchedule[]>([])                                           
  const [currentDate, setCurrentDate] = useState(Number(moment().format('DD')) > 25 ? `${moment().format('YYYY')}-${moment().format('MM')}-26` : `${moment().format('YYYY')}-${moment().subtract(1, 'month').format('MM')}-26`)
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<'abertas' | 'apontadas' | 'remoto' | 'presencial' | ''>('')
  const [receiveIsUpdate, setReceiveIsUpdate] = useState(false)
  const [projectsReport, setProjectsReport] = useState<IProjects[]>([])
  const [schedulesReport, setSchedulesReport] = useState<ISchedule[]>([])
  const [coordinators, setCoordinators] = useState<Coordinators[]>([]);
  // Analista
  const [fetchAnalystLoad, setFetchAnalystLoad] = useState(false)
  const [analistas, setAnalistas] = useState<IAnalyst[]>([])
  const [analyst, setAnalyst] = useState('')

  const history = useHistory()
  const { userInfo } = useUser()

  async function handleFetchAnalyst() {
    setFetchAnalystLoad(true)
    const token = await sessionStorage.getItem('token')

    try {
      const { data } = await analystService.index(token!)
      if (data.status === 'ok') {
        setAnalistas(sortBy(data.dados, ['nome']))
      } else {
        toast.error(data.msg)
      }
    } catch (error) {
      toast.error('Ops algo deu errado')
      console.log(error)
    } finally {
      setFetchAnalystLoad(false)
    }
  }

  const handleEventClick = async (clickInfo: EventClickArg) => {
    const agenda = fullCalendarData.filter(item => item.key === clickInfo.event.extendedProps.key)[0]
    const analista =  (userInfo.cargo === 'C' && analyst !== "") ? analyst : userInfo.codigo;

    if (agenda.os === '' && agenda.tarefa === "" && agenda.status === "3") {
      return toast.error('Essa agenda não pode ser apontada.')
    }

    if(clickInfo.event.extendedProps.codcli.includes('99999')) {
      return toast.error('Agenda Bloqueada.')
    } 

    const token = await sessionStorage.getItem('token')

    async function deleteOs() {
      try {
        setIsLoading(true)
        const { data, status } = await osService.deleteOS(agenda.os!, token!)

        const { msg } = data
        
        if (status === 200 || data.status === 'ok') {
          toast.success(msg)
          const analista =  (userInfo.cargo === 'C' && analyst !== "") ? analyst : userInfo.codigo
          const { data: getOsResponse } = await osService.getOs(agenda.key, analista, token!)
          if (getOsResponse.status === 'ok') {
            await updateSchedulesData(getOsResponse.analista)
          } else {
            toast.error('Ops... Algo deu errado tente novamente.')
            window.location.reload();
          }
        } else {
          toast.error(msg || data.message)
        }
      } catch (error: any) {
        if (error.response) {
          toast.error(error.response.data.errorMessage)
        }
        console.log(error)
      } finally {
        setIsLoading(false)
        setReceiveIsUpdate(false)
      }

    }
    let swalOption = {}
    if (agenda.os === '' && agenda.tarefa === "") {
      swalOption = {
        html: `
        <h1 style="text-align: left; font-size: 2.5rem; margin-bottom: 1rem;">Lançar OS</h1>
          <div style="text-align: left; font-size: 1.5rem;">
          <p><strong style="color: #2778c4; display: inline;">DATA</strong>: ${moment(agenda.data).format('DD/MM/YYYY')}</p>
          <p><strong style="color: #2778c4; display: inline;">LOCAL</strong>: ${agenda.local}</p>
          <p><strong style="color: #2778c4; display: inline;">EMPRESA</strong>: ${agenda.nomecli}</p>
          <p><strong style="color: #2778c4; display: inline;">PROJETO</strong>: ${agenda.projeto}</p>
          </div>
        `,
        showCloseButton: true,
        confirmButtonText: 'Lançar OS',
        confirmButtonColor: '#2778c4'
      }
    } else {
      swalOption = {
        title: 'Opções da OS',
        text: 'Escolha uma das opções para fazer com OS',
        icon: 'question',
        showConfirmButton: true,
        showCancelButton: true,
        showDenyButton: true,
        showCloseButton: true,
        confirmButtonText: agenda.status !== "3" ? 'Opções' : 'Despesas',
        cancelButtonText: 'Emitir PDF',
        denyButtonText: agenda.status !== "3" ? 'Despesas' : `<a style="text-decoration: none; color: #fff;" href="/PDF/${agenda.key}/${analista}/${token}" target="_blank">Visualizar OS</a>`,
        denyButtonColor: agenda.status !== "3" ? '#DAA520' : '#2778c4',
        confirmButtonColor: agenda.status !== "3" ? '#2778c4' : '#DAA520',
        cancelButtonColor: '#70c386'
      }
    }
    Swal.fire(swalOption).then(async (result) => {
      if (result.isDismissed && (typeof (result.dismiss) === 'string' && (result.dismiss === "close" || result.dismiss === "backdrop"))) {
        return
      }

      if (result.isConfirmed && agenda.os !== '') {
        if (agenda.local === 'REMOTO' && agenda.status === "3") {
          toast.error('Agenda remoto não possui despesa.')
          return
        }

        if (agenda.status === "3") {
          setLaunchOsModalData(agenda)
          setIsOpenOutgoingOSModal(true)
          setIsVisualOutgoingOSModal(true)
          // return toast.error('Não é possível excluir ou alterar OS depois do período de fechamento.')
          return
        }
        if (result.isConfirmed) {
          Swal.fire({
            // icon: 'info',
            title: `OS - ${agenda.os}`,
            html: `
              <div style="text-align: left; font-size: 1.5rem;">
              <p><strong style="color: #2778c4; display: inline;">DATA</strong>: ${moment(agenda.data).format('DD/MM/YYYY')}</p>
              <p><strong style="color: #2778c4; display: inline;">LOCAL</strong>: ${agenda.local}</p>
              <p><strong style="color: #2778c4; display: inline;">EMPRESA</strong>: ${agenda.nomecli}</p>
              <p><strong style="color: #2778c4; display: inline;">PROJETO</strong>: ${agenda.projeto}</p>
              </div>
            `,
            showCloseButton: true,
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Alterar OS',
            cancelButtonText: 'Excluir OS',
            denyButtonText: `<a style="text-decoration: none; color: #fff;" href="/PDF/${agenda.key}/${analista}/${token}" target="_blank">Visualizar OS</a>`,
            denyButtonColor: '#DAA520',
            cancelButtonColor: '#D33',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading()
          }).then(async (result) => {
            if (result.isConfirmed) {
              setLaunchOsModalData(agenda)
              setIsOpenLaunchOSModal(true)
            }
            if (result.isDismissed && (typeof (result.dismiss) === 'string' && result.dismiss === "cancel")) {
              deleteOs()
            }
            if (result.isDenied) {
              // Visualizar OS]
              // history.push('PDF', agenda.key)
            }
          })
        }
      }

      if (result.isConfirmed && agenda.os === '') {
        setLaunchOsModalData(agenda)
        setIsOpenLaunchOSModal(true)
      }
      if (result.isDismissed && (typeof (result.dismiss) === 'string' && result.dismiss === "cancel")) {
        // IMPRIMIR OS

        Swal.fire({
          title: 'Email para enviar o PDF',
          input: 'email',
          inputValue: userInfo.emailEthosx,
          inputAttributes: {
            autocapitalize: 'off'
          },
          showCancelButton: true,
          confirmButtonText: 'Enviar',
          showLoaderOnConfirm: true,
          allowOutsideClick: () => !Swal.isLoading()
        }).then(async (result) => {
          console.log(agenda)
          if (result.isConfirmed) {
            const { data } = await osService.getOs(Number(agenda.key), analista, token!)

            if (data.status === 'ok') {
            
              const blob = await pdf(<MyPdf data={data.analista.agendas[0]} name={data.analista.nome} signature={data.analista.assinatura} />).toBlob();
              const pdf64 = await getBase64(blob)
              try {
                // setIsLoading(true)
                const objectParams = {
                  email: result.value,
                  cc: "",
                  cco: "",
                  subject: `Os analista - ${analystName}`,
                  body: `Os analista - ${analystName}`,
                  attach: [{ data: pdf64, name: `${analystName}OS.PDF` }],
                  token: await sessionStorage.getItem('token'),
                };

                const { data } = await osService.sendPdfOS(objectParams)

                const { status, msg } = data

                if (status === 'ok') {
                  toast.success(msg)
                } else {
                  toast.error(msg)
                  return
                }
              } catch (error: any) {
                if (error.response) {
                  toast.error(error.response.data.errorMessage)
                }
                console.log(error)
              } finally {
                // setIsLoading(false)
              }
            }
          }
        })
      }
      if (result.isDenied && agenda.status !== "3") {
        // DESPESAS OS
        if (agenda.local === 'REMOTO') {
          toast.error('Agenda remoto não possui despesa.')
          return
        }

        if (agenda.os === '') {
          toast.error('OS não lançada.')
          return
        }

        setLaunchOsModalData(agenda)
        setIsOpenOutgoingOSModal(true)
        setIsVisualOutgoingOSModal(false)
      }
    });
  }

  function renderEventContent(eventContent: EventContentArg) {
    return (
      <Os 
        os={eventContent.event._def.extendedProps.os}
        nomeCoordenador={eventContent.event._def.extendedProps.nomeCoordenador}
        local={eventContent.event._def.extendedProps.local} 
        title={eventContent.event.title}
        subTitle={eventContent.event._def.extendedProps.projeto}
        color={eventContent.event._def.extendedProps.osColor}
        hour={eventContent.event._def.extendedProps.horatot}
      />
    )
  }

  async function loadSchedulesData(month: number, year: number, analystCode: string = analyst) {
    try {
      setIsLoading(true)
      const userToken = await sessionStorage.getItem('token') || await localStorage.getItem('ethosxAnalista:token')
      const { data: schedulesResponse } = await scheduleService.index(userToken!, month, year, false, analystCode)
      const { analista, msg, status } = schedulesResponse
      setSchedulesInfo(analista)
      if (status === 'ok') {
        await sessionStorage.setItem('analystName', analista.nome)
        setAnalystName(analista.nome)
        const analistaOs: ISchedule[] = []
        analista.agendas.map((item: ISchedule) => {
          let addAgenda = true;
          item.title = `${item.nomecli}`;
          item.start = item.data
          item.osColor = item.color === '' ? '#888' : item.color
          item.color = 'transparent'

          analistaOs.push(item)

          return item
        })
        
        const key = 'nomeCoordenador';
        const arrayUniqueByKey = [...new Map(analista.agendas.map((item:ISchedule) =>[item[key], { nome: item.nomeCoordenador.split(' ')[0], color: item.osColor! }])).values()];
        setCoordinators(arrayUniqueByKey as Coordinators[])
        
        const date = moment(analista.currentDate).subtract('months', 1).set('date', 25).format('YYYY-MM-DD')//Number(moment().format('DD')) > 25 ? moment(analista.currentDate).set('date', 26).format('YYYY-MM-DD') : moment(analista.currentDate).subtract(1, 'month').set('date', 26).format('YYYY-MM-DD')
        setCurrentDate(date)

        setFullCalendarData(analistaOs)
        setSchedules(analistaOs)

        const currentMonth = Number(moment().format('DD')) > 25 ? (Number(moment().format('MM')) + 1) > 12 ? 1 : (Number(moment().format('MM')) + 1) : Number(moment().format('MM'));
        const currentYear = Number(moment().format('DD')) > 25 && (Number(moment().format('MM')) + 1) > 12 ? (Number(moment().format('YYYY')) + 1) : Number(moment().format('YYYY'));
        
        setReceiveIsUpdate(true)
        if(analista.agendas.filter((item: ISchedule) => item.os === '' && moment(item.data).isBefore(moment(new Date()).format('YYYY-MM-DD'))).length > 0 && month === currentMonth && year === currentYear) {
          Swal.fire({
            title: 'Atenção',
            html: `<span style="font-size: 14px;">Você tem <strong>${analista.agendas.filter((item: ISchedule) => item.os === '' && moment(item.data).isBefore(moment(new Date()).format('YYYY-MM-DD'))).length}</strong> agenda atrasada! Por favor aponte as agendas antes do fechamento do mês.</span>`,
            icon: 'warning',
            confirmButtonText: 'Ok'
          })
        }
      } else {
        toast.error(msg)
      }

    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data.errorMessage)
      }
      await localStorage.removeItem('ethosxAnalista:token')
      await sessionStorage.removeItem('analystName')
      await sessionStorage.removeItem('token')

      toast.error('Algo deu errado faça login novamente.')
      history.push('/login')
    } finally {
      setIsLoading(false)
    }
  }

  async function loadReportData(month: number, year: number) {
    try {
      setIsLoading(true)
      setProjectsReport([])
      const userToken = await sessionStorage.getItem('token') || await localStorage.getItem('ethosxAnalista:token')
      const { data: schedulesResponse } = await scheduleService.index(userToken!, month, year, true)
      const { analista, msg, status } = schedulesResponse

      if (status === 'ok') {

        const analistaOs: ISchedule[] = []
        const projectOS: IProjects[] = []
        analista.agendas.map((item: ISchedule) => {
          if (item.horatot === 0) {
            item.title = analista.cargo === 'C' ? `${item.analista} - ${item.nomecli} - ${item.projeto}` : `${item.nomecli} - ${item.projeto}`;
          } else {
            item.title = `${item.horatot} - ${item.nomecli}`;
          }
          item.start = item.data

          analistaOs.push(item)
          if (item.os !== '') {
            const projectExist = projectOS.find(project => project.nome === item.projeto)
            if (projectExist === undefined) {
              projectOS.push({
                nome: item.projeto,
                valor: item.valortot,
                horas: item.horatot,
                despesa: item.totDesp
              })
            } else {
              const index = projectOS.findIndex(project => project.nome === item.projeto)

              projectOS[index].valor = projectOS[index].valor + item.valortot
              projectOS[index].horas = projectOS[index].horas + item.horatot
              projectOS[index].despesa = projectOS[index].despesa + item.totDesp
            }
          }
          // analistaOs.push(item)

          return item
        })
        setProjectsReport(projectOS)
        setSchedulesReport(analistaOs)
      } else {
        toast.error(msg)
      }

    } catch (error: any) {
      console.log(error)
      if (error.response) {
        toast.error(error.response.data.errorMessage)
      }
      await localStorage.removeItem('ethosxAnalista:token')
      await sessionStorage.removeItem('analystName')
      await sessionStorage.removeItem('token')

      toast.error('Algo deu errado faça login novamente.')
      history.push('/login')
    } finally {
      setIsLoading(false)
    }
  }

  async function updateSchedulesData(analista: IScheduleStruct) {
    const newFullCalendarData: ISchedule[] = []
    const newSchedules: ISchedule[] = []
    analista.agendas.map((item: ISchedule) => {
      item.title = `${item.nomecli}`;
      item.start = item.data
      item.osColor = item.color === '' ? '#888' : item.color
      item.color = 'transparent'
      item.start = item.data
      fullCalendarData.map(data => {
        if (data.key === item.key) {
          newFullCalendarData.push(item)
        } else {
          newFullCalendarData.push(data)
        }
        return data
      })

      schedules.map(schedule => {
        if (schedule.key === item.key) {
          newSchedules.push(item)
        } else {
          newSchedules.push(schedule)
        }
        return schedule
      })
      return item
    })
    // setCurrentDate(analista.currentDate)
    setFullCalendarData(newFullCalendarData)
    setSchedules(newSchedules)
    filterSchedules(filter, newSchedules)
  }

  async function filterSchedules(type: 'abertas' | 'apontadas' | 'remoto' | 'presencial' | '', data: ISchedule[]) {
    setIsLoading(true)
    setFilter(type)
    if (type === 'abertas') {
      const filterSchedulesAbertas = data.filter(item => item.os === '')
      setFullCalendarData(filterSchedulesAbertas)
    }
    if (type === 'apontadas') {
      const filterSchedulesApontadas = data.filter(item => item.os !== '')
      setFullCalendarData(filterSchedulesApontadas)
    }
    if (type === 'remoto') {
      const filterSchedulesRemota = data.filter(item => item.local === 'REMOTO')
      setFullCalendarData(filterSchedulesRemota)
    }
    if (type === 'presencial') {
      const filterSchedulesPresencial = data.filter(item => item.local !== 'REMOTO')
      setFullCalendarData(filterSchedulesPresencial)
    }
    if (type === '') {
      setFullCalendarData(data)
    }
    if (filter === type) {
      setFilter('')
      setFullCalendarData(data)
    }
    setIsLoading(false)
  }

  // LauchOSModal
  const handleCloseLaunchOSModal = () => {
    setIsOpenLaunchOSModal(false)
  }

  const handleCloseOutgoingOSModal = () => {
    setIsOpenOutgoingOSModal(false)
    setIsVisualOutgoingOSModal(false)
  }

  useEffect(() => {
    setCalendarHeight(window.innerHeight)
  }, [])

  return (
    <ScheduleContext.Provider value={{
      month,
      year,
      analystName,
      calendarHeight,
      isOpenLaunchOSModal,
      isOpenOutgoingOSModal,
      isVisualOutgoingOSModal,
      launchOsModalData,
      fullCalendarData,
      schedules,
      coordinators,
      filter,
      setFilter,
      currentDate,
      isLoading,
      handleEventClick,
      renderEventContent,
      loadSchedulesData,
      schedulesInfo,
      handleCloseLaunchOSModal,
      handleCloseOutgoingOSModal,
      setMonth,
      setYear,
      setFullCalendarData,
      setCurrentDate,
      setIsLoading,
      updateSchedulesData,
      filterSchedules,
      receiveIsUpdate,
      setReceiveIsUpdate,
      loadReportData,
      projectsReport,
      schedulesReport,
      analistas,
      fetchAnalystLoad,
      handleFetchAnalyst,
      setAnalyst,
      analyst
    }}>
      {children}
    </ScheduleContext.Provider>
  )
}

function useSchedule() {
  const context = useContext(ScheduleContext)

  return context
}

export { ScheduleProvider, useSchedule }